import { CircularProgress, Dialog, DialogContent } from "@mui/material";
import MDBadge from "components/Basics/MDBadge";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import vectorActions from "redux-react/actions/vectorActions";
import { socket } from "redux-react/middleware/ws";

const STEPS_STATE = {
	WAITING: "waiting",
	RUNNING: "running",
	DONE: "done"
};

const VectorMigrationDialog = ({ open, onClose, selectedVectorMigration, loadConfig }) => {
	const dispatch = useDispatch();
	const [migrationRunning, setMigrationRunning] = useState(false);
	const [migrationFinished, setMigrationFinished] = useState({
		finish: false,
		success: false
	});

	const [steps, setSteps] = useState([]);

	const updateStep = ({ code, percent, state }) => {
		setSteps(prevSteps => {
			return prevSteps.map(s => {
				if (s.code === code) {
					return { ...s, percent, state };
				}
				return s;
			});
		});
	};

	const migrationFinish = ({ success }) => {
		if (success) {
			loadConfig();
		}

		setMigrationFinished({
			finish: true,
			success
		});
	};

	useEffect(() => {
		socket.on("vector_migration_progress", updateStep);
		socket.on("vector_migration_finish", migrationFinish);
		return () => {
			socket.off("vector_migration_progress", updateStep);
			socket.off("vector_migration_finish", migrationFinish);
		};
	}, [migrationRunning, migrationFinished]);

	const startMigration = () => {
		setMigrationFinished(false);
		setMigrationRunning(true);

		setSteps(prev => [
			{
				code: "step1",
				title: t("CONFIG.vectorMigration.TitleStep1"),
				percent: 0,
				state: STEPS_STATE.WAITING
			},
			{
				code: "step2",
				title: t("CONFIG.vectorMigration.TitleStep2"),
				percent: 0,
				state: STEPS_STATE.WAITING
			},
			{
				code: "step3",
				title: t("CONFIG.vectorMigration.TitleStep3"),
				percent: 0,
				state: STEPS_STATE.WAITING
			}
		]);

		dispatch(
			vectorActions.startVectorMigration({
				vectorModel: selectedVectorMigration.vectorModel,
				embeddingsModel: selectedVectorMigration.embeddingsModel
			})
		);
	};

	const getFinishComponent = () => {
		if (migrationFinished.success === true) {
			return (
				<MDBox bgColor="success" p={3} borderRadius="lg" textAlign="center">
					<MDTypography variant="h6" color="white">
						{t("CONFIG.vectorMigration.migrationFInish")}
					</MDTypography>
				</MDBox>
			);
		} else {
			return (
				<MDBox bgColor="error" p={3} borderRadius="lg" textAlign="center">
					<MDTypography variant="h6" color="white">
						{t("CONFIG.vectorMigration.migrationFInishError")}
					</MDTypography>
				</MDBox>
			);
		}
	};

	const getComponentContent = () => {
		if (migrationRunning === false) {
			return (
				<MDBox
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					p={3}
					sx={{ height: "100%" }}
				>
					<MDTypography>{t("CONFIG.vectorMigration.confirmMigration")}</MDTypography>
					<MDBox
						display="flex"
						flexDirection="row"
						justifyContent="center"
						alignItems="center"
						mt={2}
					>
						<MDButton variant="outlined" color="info" sx={{ mr: 1 }} onClick={onClose}>
							{t("SETTINGS.cancel")}
						</MDButton>
						<MDButton
							variant="gradient"
							color="info"
							sx={{ ml: 1 }}
							onClick={() => {
								startMigration();
							}}
						>
							{t("SETTINGS.confirm")}
						</MDButton>
					</MDBox>
				</MDBox>
			);
		} else {
			return (
				<MDBox display="flex" flexDirection="column">
					<MDTypography variant="subtitile2" mb={2} sx={{ textAlign: "center" }}>
						{t("CONFIG.vectorMigration.migrationRunning")}
					</MDTypography>
					{steps.map((step, index) => (
						<StepBar key={index} state={step.state} percent={step.percent} title={step.title} />
					))}
					{migrationFinished?.finish && (
						<MDBox mt={3} textAlign="center">
							{getFinishComponent()}
							<MDBox display="flex" flexDirection="row" justifyContent="end">
								<MDButton
									sx={{ mt: 2 }}
									variant="gradient"
									color="info"
									onClick={() => {
										onClose();
										setMigrationRunning(false);
										setMigrationFinished({ finish: false, success: false });
									}}
								>
									{t("SETTINGS.close")}
								</MDButton>
							</MDBox>
						</MDBox>
					)}
				</MDBox>
			);
		}
	};

	return (
		<Dialog fullWidth maxWidth="lg" open={open}>
			<DialogContent>{getComponentContent()}</DialogContent>
		</Dialog>
	);
};

const StepBar = ({ state, title, percent }) => {
	const getBadge = () => {
		switch (state) {
			case STEPS_STATE.WAITING:
				return (
					<MDBadge
						size="md"
						badgeContent={t("CONFIG.vectorMigration.waiting")}
						container
						color="light"
						variant="contained"
					/>
				);
			case STEPS_STATE.RUNNING:
				return (
					<MDBadge
						size="md"
						badgeContent={`${percent}%`}
						container
						color="info"
						variant="contained"
					/>
				);
			case STEPS_STATE.DONE:
				return (
					<MDBadge
						size="md"
						badgeContent={t("CONFIG.vectorMigration.finished")}
						container
						color="success"
						variant="contained"
					/>
				);
			default:
				return null;
		}
	};

	return (
		<MDBox
			bgColor={state === STEPS_STATE.RUNNING ? "info" : "dark"}
			p={2}
			mt={1}
			mb={1}
			borderRadius="lg"
			display="flex"
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
		>
			<MDBox display="flex" flexDirection="row" alignItems="center">
				<CircularProgress
					size={24}
					thickness={4}
					disableShrink
					color={state === STEPS_STATE.RUNNING ? "white" : "secondary"}
					value={0}
					variant={state === STEPS_STATE.DONE ? "determinate" : "indeterminate"}
				/>
				<MDTypography variant="h6" color="white" ml={2}>
					{title}
				</MDTypography>
			</MDBox>
			{getBadge()}
		</MDBox>
	);
};

export default VectorMigrationDialog;
